/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import React from 'react';
import { Box, Grid, HStack, NativeSelect, Select, Text, createListCollection, Portal } from '@chakra-ui/react';
import {
  COST_CURRENCY_NOT_CONVERTED,
  DEMAND_SOURCE_COST_TYPES,
  DEMAND_SOURCE_COST_TYPE_LABELS,
  DEMAND_SOURCE_PRICE_TYPES,
  DEMAND_SOURCE_PRICE_TYPE_LABELS,
  costValueProblem,
  describeCostPreview,
  fixedPriceOf,
  maxFetchRoundsProblem,
  priceValueProblem,
  readDemandSourcePrice,
  weightValueProblem,
  type DemandSourceCost,
  type DemandSourceCostType,
  type DemandSourcePriceType,
} from '@demand-source-cost';
import type { BoundMember } from '@artifact-terms';
import type { TermMember } from '@artifact-terms';
import { NumberField } from '../inputs/NumberField';
import { ListField } from '../inputs/ListField';
import { padDecimals } from '../inputs/NumberField.helpers';
import { FieldHelpTooltip } from '../../ui/FieldHelpTooltip';
import { FormField, FieldFindingMark } from '../inputs/FormField';
import { issueLevel, type MappingIssue } from './mapping-issues';
import {
  BOUND_LABELS,
  boundMarkText,
  provenanceSentence,
  restrictionIssues,
  restrictionsToPayload,
  statedMembers,
  termMarkText,
  termsFieldFindings,
  termsToPayload,
  type InheritedTerms,
  type RestrictionsFormValue,
  type TermsFormValue,
  type TermsSubject,
} from './terms-editor.adapters';

const costTypeCollection = createListCollection({
  items: DEMAND_SOURCE_COST_TYPES.map((type) => ({ label: DEMAND_SOURCE_COST_TYPE_LABELS[type], value: type })),
});

const FOCUS = { borderColor: 'accent.solid', boxShadow: '0 0 0 1px var(--chakra-colors-accent-solid)' };

interface TermsEditorProps {
  value: TermsFormValue;
  onChange: (next: TermsFormValue) => void;
  /**
   * **What a blank member inherits** — `NOTHING_INHERITED` on the base tier. Required rather than
   * optional (`#1134`): the box is exactly the resolved tier (`ST2`), and a surface that forgot to say
   * what is beneath it would render every blank member as *no value*, which is the false claim this
   * slice exists to remove.
   */
  inherited: InheritedTerms;
  /** Whose terms these are, for the sentences that name them. */
  subject: TermsSubject;
  /**
   * The `P3a` chip for a member, where the tier being edited **extends** a parent; `null` where it
   * cannot (Standard Terms, a new source). A different fact from the in-force mark: `P3a` says this
   * document took the value from its parent document, the mark says which **tier** of the resolution
   * answered (`ST4`).
   */
  parentMark: ((member: TermMember) => React.ReactNode) | null;
}

/**
 * **The one Terms box** (`3c-ii`, `#1398`; `ST2`) — cost, price, weight, required and max rounds, and
 * the ad restrictions (`5c-i`, `#1321`).
 *
 * **The box is exactly the resolved tier.** Everything in it may come from somewhere beneath, and
 * nothing outside it does, so *where did this come from* is answered once for the region — the line at
 * the top — with a mark on each member that differs from what a reader would assume: *From Standard
 * Terms* on a blank member, *Overrides Standard Terms* on a stated one. A stated value that overrides
 * nothing carries no mark, because the filled box already says it is this tier's own.
 *
 * One component for every tier that states terms: a demand source in the wizard, and *Standard Terms*
 * on the Configuration page (`ST6b`). Two editors would drift in what a term even is (`ST6`, Todd).
 */
export const TermsEditor: React.FC<TermsEditorProps> = ({ value, onChange, inherited, subject, parentMark }) => {
  const stated = statedMembers(value);
  const { cost } = value;
  const setCost = (patch: Partial<TermsFormValue['cost']>) => onChange({ ...value, cost: { ...cost, ...patch } });
  const isShare = cost.type === 'revShare';
  const costProblem = costValueProblem(cost.type, cost.value);

  /** The mark for one member, rendered under its control, or nothing. */
  const mark = (member: TermMember): React.ReactNode => {
    const text = termMarkText(member, stated.has(member), inherited.terms[member]);
    return text ? (
      <Text fontSize="xs" color="accent.ink" mt={1} data-testid={`term-mark-${member}`}>
        {text}
      </Text>
    ) : null;
  };
  const chip = (member: TermMember) => parentMark?.(member) ?? null;

  // What is **in force** for the preview: the tier's own value where it states one, else the one
  // beneath. The preview is the optimizer's arithmetic, and the optimizer reads the resolved terms.
  const payload = termsToPayload(value);
  const costInForce: DemandSourceCost | undefined = payload.cost ?? inherited.terms.cost?.value;
  const priceInForce = payload.price ?? inherited.terms.price?.value;
  const requiredInForce = value.required ?? inherited.terms.required?.value ?? false;
  // An invalid value previews nothing: the problem line says why, and a sentence about *no cost* would
  // be false while the operator is typing one.
  const example = costProblem
    ? null
    : costInForce
      ? describeCostPreview(costInForce, fixedPriceOf(readDemandSourcePrice(priceInForce)), requiredInForce)
      : noCostSentence(subject);

  // A blank box shows what it would inherit, where the inherited value is the same kind of number —
  // a 20% share must not read as a $20 CPM because the type select sits on its default.
  const inheritedCost = inherited.terms.cost?.value;
  const inheritedPrice = inherited.terms.price?.value;
  const costPlaceholder =
    inheritedCost && inheritedCost.type === cost.type
      ? isShare
        ? String(inheritedCost.value)
        : padDecimals(inheritedCost.value, 2)
      : isShare
        ? '20'
        : '0.00';
  const pricePlaceholder =
    inheritedPrice && inheritedPrice.type === value.price.type ? padDecimals(inheritedPrice.value, 2) : '0.00';
  const inheritedWeight = inherited.terms.weight?.value;
  const inheritedRounds = inherited.terms.maxFetchRounds?.value;
  const inheritedRequired = inherited.terms.required;

  const priceProblem = priceValueProblem(value.price.value);
  const weightProblem = weightValueProblem(value.weight);
  const roundsProblem = maxFetchRoundsProblem(value.maxFetchRounds);
  // Non-blocking findings on the box's own members (`#1456`), from the one function every tier reads.
  const findings = termsFieldFindings(value, inherited);

  return (
    <Box border="1px solid" borderColor="border.default" borderRadius="8px" p={3} data-testid="terms-box">
      <Text fontSize="xs" color="fg.muted" mb={3} data-testid="terms-provenance">
        {provenanceSentence(subject, inherited)}
      </Text>

      {/* **The money on one row** (`3h`, `#1409`): what we pay, what the auction uses, and how the
          source ranks, which are read against each other. `LP11`'s rule — short related fields share a
          row, and the field is what gets bounded — so a wide screen makes the box shorter. */}
      <Grid templateColumns={MONEY_ROW} gap={4} alignItems="start">
        {/* ── Cost ── what we pay (`ST3`: a deduction from what a bid is worth to us). The currency
            is the unit beside the number, and that it is never converted is behind the `?`. */}
        <FormField
          label="Cost"
          mark={chip('cost')}
          help={`What we pay ${subject === 'standard' ? 'a source' : 'this source'}. The optimizer ranks its bids by what is left. ${COST_CURRENCY_NOT_CONVERTED}`}
          error={costProblem ?? undefined}
        >
          <HStack gap={2} align="center">
            <Select.Root
              size="sm"
              w="130px"
              flexShrink={0}
              value={[cost.type]}
              onValueChange={(details) => setCost({ type: details.value[0] as DemandSourceCostType })}
              collection={costTypeCollection}
            >
              <Select.HiddenSelect aria-label="Cost type" />
              <Select.Trigger borderColor="border.default">
                <Select.ValueText />
              </Select.Trigger>
              <Portal>
                <Select.Positioner>
                  <Select.Content>
                    {costTypeCollection.items.map((item) => (
                      <Select.Item key={item.value} item={item}>
                        {item.label}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Positioner>
              </Portal>
            </Select.Root>
            {!isShare && (
              <Text fontSize="sm" color="fg.muted" data-testid="cost-unit">
                {currencySymbol(cost.currency)}
              </Text>
            )}
            <NumberField
              minDecimals={isShare ? null : 2}
              size="sm"
              min={0}
              max={isShare ? 100 : undefined}
              step={isShare ? 1 : 0.01}
              value={cost.value}
              onChange={(next) => setCost({ value: next })}
              placeholder={costPlaceholder}
              aria-label={isShare ? 'Cost, percent of the bid' : 'Cost, CPM'}
              aria-invalid={costProblem ? true : undefined}
              borderColor="border.default"
              _focus={FOCUS}
              w="100%"
            />
            {isShare && (
              <Text fontSize="sm" color="fg.muted" data-testid="cost-unit">
                %
              </Text>
            )}
          </HStack>
          {mark('cost')}
          {/* Under Cost, in its column: it is the arithmetic of this one field (`3h` rehearsal). */}
          {example && (
            <Text fontSize="xs" color="fg.muted" mt={1} data-testid="cost-example">
              {example}
            </Text>
          )}
        </FormField>

        {/* ── Price ── the label follows the type, because the number means two different things
            and the operator has to read which one is in force without knowing the rule (`ST3`). */}
        <FormField
          label={value.price.type === 'fixed' ? 'Fixed Price ($)' : 'CPM Floor ($)'}
          mark={chip('price')}
          help={
            value.price.type === 'fixed'
              ? 'The agreed price. Sent with every request and recorded as the price of every ad the source fills — no strategy or round plan may move it.'
              : 'The least the source may be asked to bid. Sent with every request; a strategy or round plan may ask for more but never less.'
          }
          error={priceProblem ?? undefined}
          finding={findings.price ?? null}
        >
          <HStack gap={2} align="center">
            <NativeSelect.Root size="sm" w="120px" flexShrink={0}>
              <NativeSelect.Field
                aria-label="Price type"
                value={value.price.type}
                onChange={(e) =>
                  onChange({
                    ...value,
                    price: { ...value.price, type: e.currentTarget.value as DemandSourcePriceType },
                  })
                }
                borderColor="border.default"
              >
                {DEMAND_SOURCE_PRICE_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {DEMAND_SOURCE_PRICE_TYPE_LABELS[type]}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
            <NumberField
              minDecimals={2}
              value={value.price.value}
              onChange={(next) => onChange({ ...value, price: { ...value.price, value: next } })}
              placeholder={pricePlaceholder}
              size="sm"
              min={0}
              step={0.01}
              aria-label="Price, CPM"
              aria-invalid={priceProblem ? true : undefined}
              borderColor="border.default"
              _focus={FOCUS}
              w="100%"
            />
          </HStack>
          {mark('price')}
        </FormField>

        {/* ── Weight ── a signed percent (`#937`): selection only, never what an ad earns. */}
        <FormField
          label="Weight (%)"
          mark={chip('weight')}
          help="Boosts or reduces the source's ads when the pod is chosen: 20 ranks them as if worth 20% more, −10 as 10% less. It changes which ads are chosen, never what they earn."
          error={weightProblem ?? undefined}
        >
          <NumberField
            minDecimals={null}
            value={value.weight}
            onChange={(weight) => onChange({ ...value, weight })}
            size="sm"
            min={-100}
            step={1}
            placeholder={inheritedWeight !== undefined ? String(inheritedWeight) : '0'}
            aria-label="Weight, percent"
            aria-invalid={weightProblem ? true : undefined}
            borderColor="border.default"
            _focus={FOCUS}
            w="100%"
          />
          {mark('weight')}
        </FormField>
      </Grid>

      <Grid templateColumns={MONEY_ROW} gap={4} mt={4} alignItems="start">
        {/* ── Required ── three answers, because *not required* and *says nothing* differ once a
            tier beneath can say *required* (`ST4`): a source may override a partner's `true`. */}
        <FormField
          label="Required"
          mark={chip('required')}
          help="A required source is served even when its ads are worth less than others — direct sold, or an inventory split."
        >
          <NativeSelect.Root size="sm">
            <NativeSelect.Field
              aria-label="Required source"
              value={value.required === undefined ? '' : String(value.required)}
              onChange={(e) => {
                const raw = e.currentTarget.value;
                onChange({ ...value, required: raw === '' ? undefined : raw === 'true' });
              }}
              borderColor="border.default"
            >
              <option value="">
                {inheritedRequired
                  ? `As ${inheritedRequired.from.label} says (${inheritedRequired.value ? 'required' : 'not required'})`
                  : 'Not stated (not required)'}
              </option>
              <option value="true">Required</option>
              <option value="false">Not required</option>
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
          {mark('required')}
        </FormField>

        {/* ── Max rounds ── req-loop's per-source call cap. */}
        <FormField
          label="Max Fetch Rounds"
          mark={chip('maxFetchRounds')}
          help="How many times per pod the source may be called."
          error={roundsProblem ?? undefined}
        >
          <NumberField
            minDecimals={null}
            value={value.maxFetchRounds}
            onChange={(maxFetchRounds) => onChange({ ...value, maxFetchRounds })}
            size="sm"
            min={1}
            step={1}
            placeholder={inheritedRounds !== undefined ? String(inheritedRounds) : 'Unlimited'}
            aria-label="Max fetch rounds"
            aria-invalid={roundsProblem ? true : undefined}
            borderColor="border.default"
            _focus={FOCUS}
            w="100%"
          />
          {mark('maxFetchRounds')}
        </FormField>
      </Grid>

      <Restrictions
        value={value}
        onChange={(restrictions) => onChange({ ...value, restrictions })}
        inherited={inherited}
        subject={subject}
      />
    </Box>
  );
};

/**
 * **The money row's columns** (`3h`): Cost and Price each hold a type and a number, Weight one number,
 * so the two pairs take the room. One column on a narrow screen.
 */
const MONEY_ROW = { base: '1fr', md: 'minmax(0, 1.4fr) minmax(0, 1.4fr) minmax(0, 1fr)' };
/** Two list fields side by side — the break Todd set in `#1409`, categories and advertisers. */
const PAIR_ROW = { base: '1fr', md: 'repeat(2, minmax(0, 1fr))' };

/**
 * **The ad restrictions** (`5c-i`, `#1321`; `ST5`) — bounds, not defaults, in the same box because they
 * are the same contract (`ST2`).
 *
 * The one thing that stays on the surface is the rule an operator would otherwise break: **a restriction
 * here may be made stricter, never looser** (`ST5`: *a control that silently refuses half its range is
 * `#1045`'s defect inside the affordance*). Each member's mark then says what its own value did — taken
 * from beneath, in force because it is stricter, or not applied because it would loosen it.
 *
 * **Laid out as three rows** (`3h`, `#1409`): the durations with repeat ads, the two category lists, the
 * two blocked-domain lists. **No block/allow toggle** (Todd, 2026-09-28): only categories have both
 * lists, both may be stated at once, and they combine differently across tiers (allow by
 * intersection, block by union), so a toggle would make a valid stored state impossible to show.
 */
const Restrictions: React.FC<{
  value: TermsFormValue;
  onChange: (next: RestrictionsFormValue) => void;
  inherited: InheritedTerms;
  subject: TermsSubject;
}> = ({ value, onChange, inherited, subject }) => {
  const r = value.restrictions;
  const set = (patch: Partial<RestrictionsFormValue>) => onChange({ ...r, ...patch });
  const stated = restrictionsToPayload(value);
  const issues = restrictionIssues(value, inherited);
  // **Each finding beside the fields it names** (`#1456`), not in a list at the foot of the group. A
  // clash names a pair — the two durations, or the two category lists — so it sits under their row.
  const durationIssues = issues.filter((issue) => /Shortest ad/.test(issue.ref ?? ''));
  const categoryIssues = issues.filter((issue) => !/Shortest ad/.test(issue.ref ?? ''));
  // Only a clash inside this tier blocks the save (`termsFormProblem`), so only it marks the boxes.
  const durationBlocks = durationIssues.some((issue) => issueLevel(issue) === 'error');

  const mark = (member: BoundMember): React.ReactNode => {
    const text = boundMarkText(member, stated[member], inherited.bounds[member]);
    return text ? (
      <Text fontSize="xs" color="accent.ink" mt={1} data-testid={`bound-mark-${member}`}>
        {text}
      </Text>
    ) : null;
  };
  const inheritedNumber = (member: 'minDurationSeconds' | 'maxDurationSeconds'): string | undefined => {
    const v = inherited.bounds[member]?.value;
    return v === undefined ? undefined : String(v);
  };
  const inheritedRepeats = inherited.bounds.allowRepeatAds;

  const list = (
    member: 'allowIabCategories' | 'excludeIabCategories' | 'excludeAdvertisers' | 'excludeDomains',
    placeholder: string,
    help: string,
  ) => (
    <FormField label={BOUND_LABELS[member]} help={help}>
      <ListField
        size="sm"
        value={r[member]}
        onChange={(next) => set({ [member]: next } as Partial<RestrictionsFormValue>)}
        placeholder={placeholder}
        aria-label={BOUND_LABELS[member]}
        borderColor="border.default"
        _focus={FOCUS}
      />
      {mark(member)}
    </FormField>
  );

  return (
    <Box mt={5} pt={4} borderTop="1px solid" borderColor="border.subtle" data-testid="restrictions-group">
      <HStack gap={2} align="center">
        <Text fontSize="sm" fontWeight="600" color="fg.default">
          Ad restrictions
        </Text>
        <FieldHelpTooltip
          ariaLabel="About Ad restrictions"
          content={
            subject === 'standard'
              ? "Which ads may run. Every source's ads must meet these, and a break's ads must meet the restrictions of every source selected for it."
              : "Which ads may run. A break's ads must meet the restrictions of every source selected for it, so what is set here applies to the other sources' ads in that break too."
          }
        />
      </HStack>
      <Text fontSize="xs" color="fg.muted" mt={1} data-testid="restrictions-rule">
        {subject === 'standard'
          ? 'A source, or the partner it buys under, may make these stricter, never looser.'
          : 'These may make what is beneath stricter, never looser.'}
      </Text>

      <Grid templateColumns={{ base: '1fr', md: 'repeat(3, minmax(0, 1fr))' }} gap={4} mt={3} alignItems="start">
        <FormField label={BOUND_LABELS.minDurationSeconds} help="Ads shorter than this do not run.">
          <NumberField
            minDecimals={null}
            value={r.minDurationSeconds}
            onChange={(minDurationSeconds) => set({ minDurationSeconds })}
            size="sm"
            min={0}
            step={1}
            placeholder={inheritedNumber('minDurationSeconds') ?? 'No minimum'}
            aria-label="Shortest ad, seconds"
            aria-invalid={durationBlocks ? true : undefined}
            borderColor="border.default"
            _focus={FOCUS}
            w="100%"
          />
          {mark('minDurationSeconds')}
        </FormField>
        <FormField label={BOUND_LABELS.maxDurationSeconds} help="Ads longer than this do not run.">
          <NumberField
            minDecimals={null}
            value={r.maxDurationSeconds}
            onChange={(maxDurationSeconds) => set({ maxDurationSeconds })}
            size="sm"
            min={0}
            step={1}
            placeholder={inheritedNumber('maxDurationSeconds') ?? 'No maximum'}
            aria-label="Longest ad, seconds"
            aria-invalid={durationBlocks ? true : undefined}
            borderColor="border.default"
            _focus={FOCUS}
            w="100%"
          />
          {mark('maxDurationSeconds')}
        </FormField>
        <FormField
          label={BOUND_LABELS.allowRepeatAds}
          help="Whether the same ad (the same id, or the same creative) may run twice in one break."
        >
          <NativeSelect.Root size="sm">
            <NativeSelect.Field
              aria-label="Repeat ads"
              value={r.allowRepeatAds === undefined ? '' : String(r.allowRepeatAds)}
              onChange={(e) => {
                const raw = e.currentTarget.value;
                set({ allowRepeatAds: raw === '' ? undefined : raw === 'true' });
              }}
              borderColor="border.default"
            >
              <option value="">
                {inheritedRepeats
                  ? `As ${inheritedRepeats.from.map((ref) => ref.label).join(', ')} says (${inheritedRepeats.value ? 'allowed' : 'not allowed'})`
                  : 'Not stated (not allowed)'}
              </option>
              <option value="true">Allowed</option>
              <option value="false">Not allowed</option>
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
          {mark('allowRepeatAds')}
        </FormField>
      </Grid>
      <RestrictionFindings issues={durationIssues} testId="restrictions-findings" />

      <Grid templateColumns={PAIR_ROW} gap={4} mt={4} alignItems="start">
        {list('excludeIabCategories', 'IAB7, IAB25', 'IAB content categories that may not run, separated by commas.')}
        {list(
          'allowIabCategories',
          'Every category',
          'Only ads in these IAB categories may run. Leave blank to allow every category. An ad that declares no category is not refused.',
        )}
      </Grid>
      <RestrictionFindings issues={categoryIssues} testId="restrictions-category-findings" />

      <Grid templateColumns={PAIR_ROW} gap={4} mt={4} alignItems="start">
        {list(
          'excludeAdvertisers',
          'rival.com',
          "Advertiser domains that may not run. A domain also blocks its subdomains; 'ab.com' does not block 'cab.com'.",
        )}
        {list(
          'excludeDomains',
          'example.com',
          'Creative domains that may not run. A domain also blocks its subdomains.',
        )}
      </Grid>
    </Box>
  );
};

/**
 * **A restriction finding, under the row of fields it names** (`#1456`). A blocking one is the refusal
 * the save will give, said where the boxes are; a warning is a finding and takes the severity dot.
 */
const RestrictionFindings: React.FC<{ issues: readonly MappingIssue[]; testId: string }> = ({ issues, testId }) =>
  issues.length === 0 ? null : (
    <Box data-testid={testId}>
      {issues.map((issue) =>
        issueLevel(issue) === 'error' ? (
          <Text key={issue.message} fontSize="xs" color="error.ink" mt={1} role="alert">
            {issue.message}
          </Text>
        ) : (
          <FieldFindingMark
            key={issue.message}
            finding={{ level: issueLevel(issue) === 'warn' ? 'warn' : 'error', message: issue.message }}
          />
        ),
      )}
    </Box>
  );

/**
 * **What a missing cost means, said only where it is true** (`3c-ii`, the third of `3b`'s obligations
 * on `#1108`). *"No cost: bids from this source are valued at the full bid"* was true until a partner
 * or Standard Terms could state one; the optimizer reads the resolved cost, so this sentence is reached
 * only when no tier states any.
 */
function noCostSentence(subject: TermsSubject): string {
  return subject === 'standard'
    ? 'No cost: a source is valued at the full bid unless it, or the partner it buys under, states one.'
    : 'No cost is stated here or beneath it, so bids from this source are valued at the full bid.';
}

function currencySymbol(currency: string): string {
  return currency === 'USD' ? '$' : currency;
}
